import { SystemStyleObject } from '@chakra-ui/react';
import { Dict, memoizedGet } from '@chakra-ui/utils';
import chroma from 'chroma-js';

/*
    The following types and functions are used heavily in Chakra's default
    component theme files - however, they're not exported publicly.

    Inlining them here to support an efficient copy+paste+modify workflow when
    updating default Chakra component styles.

    Reference: 
    https://github.com/chakra-ui/chakra-ui/blob/1839e22ebad1c2a52795eac5fd0b3eb38ae03f9c/packages/styled-system/src/component.types.ts
    https://github.com/chakra-ui/chakra-ui/blob/790d2417a3f5d59e2d69229a027af671c2dc0cbc/packages/theme-tools/src/color.ts
*/

type Anatomy = { keys: string[] };

export type DefaultProps = {
    size?: string;
    variant?: string;
    colorScheme?: string;
};

export type PartsStyleObject<Parts extends Anatomy = Anatomy> = Partial<
    Record<Parts['keys'][number], SystemStyleObject>
>;

export type StyleFunctionProps = {
    colorScheme: string;
    colorMode: 'light' | 'dark';
    orientation?: 'horizontal' | 'vertical';
    theme: Dict;
    [key: string]: any;
};

export type SystemStyleFunction = (
    props: StyleFunctionProps
) => SystemStyleObject;

export type PartsStyleFunction<Parts extends Anatomy = Anatomy> = (
    props: StyleFunctionProps
) => PartsStyleObject<Parts>;

export type PartsStyleInterpolation<Parts extends Anatomy = Anatomy> =
    | PartsStyleObject<Parts>
    | PartsStyleFunction<Parts>;

export interface MultiStyleConfig<Parts extends Anatomy = Anatomy> {
    parts: Parts['keys'];
    baseStyle?: PartsStyleInterpolation<Parts>;
    sizes?: { [size: string]: PartsStyleInterpolation<Parts> };
    variants?: { [variant: string]: PartsStyleInterpolation<Parts> };
    defaultProps?: DefaultProps;
}

export const getColor = (theme: Dict, color: string): string | undefined => {
    const hex = memoizedGet(theme, `colors.${color}`, color);
    if (chroma.valid(hex)) return hex;
    if (chroma.valid(color)) return color;
    return;
};

export const transparentize =
    (colorName: string, alpha: number) =>
    (theme: Dict): string => {
        const color = getColor(theme, colorName);
        if (!color) return colorName;
        return chroma(color).alpha(alpha).hex();
    };
